import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Lottie from 'lottie-react';
import { useCareerOpenings } from '../hooks/useCareers';
import beachAnimation from '../assets/beach.json';
import meditateAnimation from '../assets/Meditate.json';
import usaMapAnimation from '../assets/USAMap.json';

const BRAND_YELLOW = '#E0A15E';
const OPENINGS_SECTION_ID = 'career-openings';

type NarrativeSection = {
  id: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  description: string;
  guidance: string;
  icon: string;
  visualTitle: string;
  visualLabel: string;
  highlights: string[];
};

const NARRATIVE_SECTIONS: NarrativeSection[] = [
  {
    id: 'work-from-the-beach',
    eyebrow: 'Remote Culture',
    title: 'Work from the beach',
    subtitle: 'We are intentionally remote-first.',
    description:
      'Cybard is built for distributed execution. We hire great people, equip them with clear systems, and trust them to deliver from wherever they do their best work.',
    guidance:
      'Outcome ownership matters more than office attendance. We optimize for communication, accountability, and results.',
    icon: 'RF',
    visualTitle: 'Remote-first by design',
    visualLabel: 'Flexible, accountable, asynchronous',
    highlights: [
      'Work from anywhere in the U.S.',
      'Structured async collaboration',
      'Clear priorities and delivery standards',
    ],
  },
  {
    id: 'work-life-balance',
    eyebrow: 'Culture',
    title: 'Work life balance',
    subtitle: 'High performance while on the clock.',
    description:
      'We take pride in our craft and we aim to be the best. During work hours, we execute with intensity and discipline so work stays at work when the day is done.',
    guidance:
      'Focused execution creates room for life outside of work. We protect both performance and personal time.',
    icon: 'WL',
    visualTitle: 'Focused execution rhythm',
    visualLabel: 'Deep work, clear handoffs, sustainable pace',
    highlights: [
      'Clear expectations and priorities',
      'Disciplined sprint execution',
      'Respect for off-clock personal time',
    ],
  },
  {
    id: 'we-hire-american',
    eyebrow: 'Hiring Policy',
    title: 'We hire American',
    subtitle: 'Cybard is an American company.',
    description:
      'Cybard is U.S.-based and we hire in the United States. We do not outsource our team to other countries.',
    guidance:
      'This keeps our operations aligned with our clients, communication standards, and long-term company mission.',
    icon: 'US',
    visualTitle: 'U.S. hiring commitment',
    visualLabel: 'Domestic team, consistent standards',
    highlights: [
      'U.S.-based hiring only',
      'No offshore outsourcing model',
      'Consistent client-facing quality and ownership',
    ],
  },
];

const NARRATIVE_LOTTIES: Partial<Record<NarrativeSection['id'], object>> = {
  'work-from-the-beach': beachAnimation,
  'work-life-balance': meditateAnimation,
  'we-hire-american': usaMapAnimation,
};

const Page = styled.main`
  background:
    radial-gradient(1200px 720px at 18% 8%, rgba(56, 189, 248, 0.32) 0%, rgba(56, 189, 248, 0) 62%),
    radial-gradient(1100px 760px at 84% 24%, rgba(168, 85, 247, 0.34) 0%, rgba(168, 85, 247, 0) 60%),
    radial-gradient(1200px 740px at 20% 50%, rgba(244, 114, 182, 0.3) 0%, rgba(244, 114, 182, 0) 62%),
    radial-gradient(1050px 700px at 82% 74%, rgba(251, 146, 60, 0.28) 0%, rgba(251, 146, 60, 0) 60%),
    radial-gradient(1200px 760px at 26% 94%, rgba(45, 212, 191, 0.3) 0%, rgba(45, 212, 191, 0) 64%),
    linear-gradient(180deg, #efecf0 0%, #efecf0 100%);
  color: #17212f;
`;

const IntroSection = styled.section`
  min-height: calc(92svh - 72px);
  padding: clamp(2.5rem, 6vw, 6rem) 1.1rem;
  display: flex;
  align-items: center;
  background: transparent;

  @media (max-width: 680px) {
    min-height: auto;
    padding: 5.5rem 0.95rem 2.4rem;
  }
`;

const IntroInner = styled.div`
  width: min(1100px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 1.1rem;
`;

const IntroCopy = styled.article`
  display: grid;
  gap: 0.95rem;
  align-content: center;
  justify-items: center;
  text-align: center;
`;

const IntroEyebrow = styled.span`
  width: fit-content;
  border: 1px solid rgba(23, 33, 47, 0.14);
  background: rgba(255, 255, 255, 0.42);
  border-radius: 999px;
  padding: 0.35rem 0.74rem;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 800;
  color: #17212f;
`;

const IntroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2rem, 5vw, 4.1rem);
  line-height: 0.98;
  letter-spacing: -0.02em;
  color: #17212f;
`;

const IntroLead = styled.p`
  margin: 0;
  font-size: clamp(1rem, 2vw, 1.18rem);
  line-height: 1.55;
  max-width: 70ch;
  color: #465263;
`;

const IntroHint = styled.p`
  margin: 0;
  font-size: 0.92rem;
  color: #5a6677;
`;

const IntroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.68rem;
  margin-top: 0.28rem;
`;

const IntroAnchor = styled.a`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: ${BRAND_YELLOW};
  color: #111827;
  font-weight: 800;
  padding: 0.68rem 1.02rem;
  font-size: 0.9rem;
`;

const CareerBand = styled.section`
  min-height: calc(100svh - 72px);
  display: flex;
  align-items: center;
  padding: clamp(2.75rem, 5vw, 5rem) 1.1rem;
  background: transparent;

  @media (max-width: 980px) {
    min-height: auto;
    padding: 2.2rem 0.95rem;
  }
`;

const BandInner = styled.div<{ $reverse: boolean }>`
  width: min(1200px, 100%);
  margin: 0 auto;
  display: grid;
  gap: clamp(1.25rem, 3vw, 3rem);
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: stretch;

  ${({ $reverse }) =>
    $reverse
      ? css`
          & > article {
            order: 2;
          }

          & > aside {
            order: 1;
          }
        `
      : null}

  @media (max-width: 980px) {
    grid-template-columns: 1fr;

    & > article,
    & > aside {
      order: initial;
    }
  }
`;

const Copy = styled.article`
  display: grid;
  gap: 0.82rem;
  align-content: center;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  padding: 0.33rem 0.68rem;
  border-radius: 999px;
  font-size: 0.76rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 800;
  color: rgba(23, 33, 47, 0.95);
  background: rgba(255, 255, 255, 0.46);
  border: 1px solid rgba(23, 33, 47, 0.14);
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(1.8rem, 3.8vw, 3rem);
  line-height: 1.06;
  letter-spacing: -0.02em;
  color: #17212f;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.04rem;
  font-weight: 700;
  color: rgba(23, 33, 47, 0.9);
`;

const Description = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.58;
  color: #465263;
  max-width: 56ch;
`;

const Guidance = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.5;
  color: #667281;
  max-width: 60ch;
`;

const Visual = styled.aside`
  border-radius: 28px;
  padding: clamp(1rem, 2.3vw, 1.5rem);
  background: rgba(255, 255, 255, 0.46);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(23, 33, 47, 0.12);
  box-shadow: 0 18px 45px rgba(23, 33, 47, 0.08);
  color: #17212f;
  display: grid;
  align-content: end;
  min-height: clamp(320px, 56vh, 520px);
  overflow: hidden;
  position: relative;

  @media (max-width: 680px) {
    border-radius: 20px;
    min-height: clamp(260px, 45vh, 360px);
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 999px;
    pointer-events: none;
  }

  &::before {
    width: 240px;
    height: 240px;
    top: -90px;
    right: -65px;
    background: rgba(214, 160, 89, 0.12);
  }

  &::after {
    width: 180px;
    height: 180px;
    left: -50px;
    bottom: -60px;
    background: rgba(23, 33, 47, 0.06);
  }
`;

const VisualHeader = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.72rem;
  margin-bottom: 0.9rem;
`;

const VisualIcon = styled.span`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-size: 1.5rem;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(23, 33, 47, 0.12);
  box-shadow: 0 10px 24px rgba(23, 33, 47, 0.08);
`;

const VisualLabel = styled.div`
  display: grid;
  gap: 0.08rem;

  strong {
    font-size: 1.03rem;
    letter-spacing: 0.01em;
    color: #17212f;
  }

  small {
    font-size: 0.82rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #667281;
  }
`;

const VisualList = styled.ul`
  position: relative;
  z-index: 1;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.55rem;
`;

const VisualListItem = styled.li`
  border: 1px solid rgba(23, 33, 47, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.58);
  padding: 0.66rem 0.74rem;
  font-size: 0.9rem;
  color: #334155;
`;

const FlatLottieVisual = styled.aside`
  min-height: clamp(320px, 56vh, 520px);
  display: grid;
  place-items: center;
  background: transparent;

  @media (max-width: 680px) {
    min-height: clamp(260px, 45vh, 360px);
  }
`;

const FlatLottieWrap = styled.div`
  width: min(100%, 820px);
  display: grid;
  place-items: center;

  > div,
  svg {
    width: 100%;
    max-height: 100%;
  }
`;

const OpeningsInner = styled.div`
  width: min(1200px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 1.1rem;
  align-content: center;
  scroll-margin-top: 88px;
`;

const OpeningsCopy = styled.div`
  display: grid;
  gap: 0.78rem;
  justify-items: center;
  text-align: center;
`;

const OpeningsDescription = styled(Description)`
  max-width: 72ch;
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: 640px) {
    justify-content: center;
  }
`;

const FilterControl = styled.label`
  display: grid;
  gap: 0.38rem;
  width: min(320px, 100%);

  @media (max-width: 640px) {
    justify-items: center;
    text-align: center;
  }
`;

const FilterLabel = styled.span`
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 800;
  color: #5f6b7a;
`;

const DepartmentSelect = styled.select`
  width: 100%;
  border: 1px solid rgba(23, 33, 47, 0.16);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.62);
  color: #17212f;
  font-size: 0.9rem;
  padding: 0.6rem 0.75rem;

  option {
    background: #efecf0;
    color: #17212f;
  }
`;

const OpeningsGrid = styled.div`
  display: grid;
  gap: 0.72rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 320px));
  justify-content: flex-start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const OpeningCard = styled.article`
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(23, 33, 47, 0.12);
  box-shadow: 0 16px 36px rgba(23, 33, 47, 0.08);
  padding: 0.92rem;
  width: min(100%, 320px);
  aspect-ratio: 1 / 1;
  display: flex;
  flex-direction: column;
  gap: 0.58rem;

  @media (max-width: 640px) {
    align-items: center;
    text-align: center;
  }
`;

const OpeningTop = styled.div`
  display: grid;
  gap: 0.3rem;

  @media (max-width: 640px) {
    justify-items: center;
    text-align: center;
  }
`;

const OpeningTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  color: #17212f;
`;

const OpeningMeta = styled.p`
  margin: 0;
  font-size: 0.76rem;
  color: #5e6a7a;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const OpeningSummary = styled.p`
  margin: 0;
  flex: 1;
  color: #465263;
  font-size: 0.9rem;
  line-height: 1.45;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
`;

const OpeningLink = styled(Link)`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  margin-top: auto;
  text-decoration: none;
  border-radius: 999px;
  background: ${BRAND_YELLOW};
  color: #111827;
  padding: 0.5rem 0.82rem;
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;

  @media (max-width: 640px) {
    align-self: center;
  }
`;

const OpeningState = styled.p`
  margin: 0;
  color: #526071;
  font-size: 0.94rem;
  text-align: center;
`;

function formatWorkType(value: string): string {
  return value.replace('_', ' ').toUpperCase();
}

export default function CareersPage() {
  const openingsQuery = useCareerOpenings();
  const openings = useMemo(() => openingsQuery.data ?? [], [openingsQuery.data]);
  const [departmentFilter, setDepartmentFilter] = useState('all');

  const departments = useMemo(
    () =>
      Array.from(
        new Set(openings.map((job) => job.team.trim()).filter((team) => team.length > 0)),
      ).sort((a, b) => a.localeCompare(b)),
    [openings],
  );

  const filteredOpenings = useMemo(
    () =>
      departmentFilter === 'all'
        ? openings
        : openings.filter((job) => job.team.trim() === departmentFilter),
    [departmentFilter, openings],
  );

  return (
    <Page>
      <IntroSection>
        <IntroInner>
          <IntroCopy>
            <IntroEyebrow>Careers</IntroEyebrow>
            <IntroTitle>Build your career with Cybard</IntroTitle>
            <IntroLead>
              Join a high-accountability team focused on execution, ownership, and meaningful client impact. If you want to do exceptional work with exceptional people, you are in the right place.
            </IntroLead>
            <IntroHint>Explore our culture and scroll to see open roles.</IntroHint>
            <IntroActions>
              <IntroAnchor href={`#${OPENINGS_SECTION_ID}`}>Explore open positions</IntroAnchor>
            </IntroActions>
          </IntroCopy>
        </IntroInner>
      </IntroSection>

      {NARRATIVE_SECTIONS.map((section, index) => {
        const reverse = index % 2 === 1;
        const sectionAnimation = NARRATIVE_LOTTIES[section.id];
        return (
          <CareerBand key={section.id} id={section.id}>
            <BandInner $reverse={reverse}>
              <Copy>
                <Eyebrow>{section.eyebrow}</Eyebrow>
                <Title>{section.title}</Title>
                <Subtitle>{section.subtitle}</Subtitle>
                <Description>{section.description}</Description>
                <Guidance>{section.guidance}</Guidance>
              </Copy>

              {sectionAnimation ? (
                <FlatLottieVisual>
                  <FlatLottieWrap aria-hidden="true">
                    <Lottie animationData={sectionAnimation} loop autoplay />
                  </FlatLottieWrap>
                </FlatLottieVisual>
              ) : (
                <Visual>
                  <VisualHeader>
                    <VisualIcon aria-hidden>{section.icon}</VisualIcon>
                    <VisualLabel>
                      <strong>{section.visualTitle}</strong>
                      <small>{section.visualLabel}</small>
                    </VisualLabel>
                  </VisualHeader>
                  <VisualList>
                    {section.highlights.map((highlight) => (
                      <VisualListItem key={highlight}>{highlight}</VisualListItem>
                    ))}
                  </VisualList>
                </Visual>
              )}
            </BandInner>
          </CareerBand>
        );
      })}

      <CareerBand id={OPENINGS_SECTION_ID}>
        <OpeningsInner>
          <OpeningsCopy>
            <Eyebrow>Current openings</Eyebrow>
            <Title>Job openings</Title>
            <Subtitle>Apply to the role that fits your strengths.</Subtitle>
            <OpeningsDescription>
              Browse open roles below. Every listing includes its requisition number so your application is mapped to the exact position.
            </OpeningsDescription>
          </OpeningsCopy>

          <FilterRow>
            <FilterControl htmlFor="careers-department-filter">
              <FilterLabel>Filter by department</FilterLabel>
              <DepartmentSelect
                id="careers-department-filter"
                value={departmentFilter}
                onChange={(event) => setDepartmentFilter(event.target.value)}
              >
                <option value="all">All departments</option>
                {departments.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </DepartmentSelect>
            </FilterControl>
          </FilterRow>

          {openingsQuery.isLoading ? <OpeningState>Loading openings...</OpeningState> : null}

          {!openingsQuery.isLoading && filteredOpenings.length === 0 ? (
            <OpeningState>No openings are published yet. Check back soon.</OpeningState>
          ) : (
            <OpeningsGrid>
              {filteredOpenings.map((job) => (
                <OpeningCard key={job.id}>
                  <OpeningTop>
                    <OpeningTitle>{job.title}</OpeningTitle>
                    <OpeningMeta>
                      {job.requisitionId} - {job.team} - {job.location} - {formatWorkType(job.workType)}
                    </OpeningMeta>
                  </OpeningTop>
                  <OpeningSummary>{job.summary}</OpeningSummary>
                  <OpeningLink to={`/careers/${job.slug}`}>View role</OpeningLink>
                </OpeningCard>
              ))}
            </OpeningsGrid>
          )}
        </OpeningsInner>
      </CareerBand>
    </Page>
  );
}
